/**
 * BlockParry — pure block and parry frame-window logic.
 *
 * Hold BLOCK → chip damage only (BLOCK_CHIP_RATIO of full).
 * Tap BLOCK within PARRY_WINDOW frames before a hit → full parry (0 damage).
 * After a parry, PARRY_COUNTER_WINDOW frames of free-counter with bonus damage.
 */

/** Frames before impact during which a BLOCK tap counts as a parry. */
export const PARRY_WINDOW = 6;

/** Fraction of damage that bleeds through a held block. */
export const BLOCK_CHIP_RATIO = 0.2;

/** Frames of free-counter window granted after a successful parry. */
export const PARRY_COUNTER_WINDOW = 60;

/** Damage multiplier applied to attacks thrown during the counter window. */
export const PARRY_BONUS_MULT = 1.5;

/** Frames all entity updates are frozen on parry (hitpause / screen freeze). */
export const SCREEN_FREEZE_FRAMES = 4;

export interface BlockParryState {
  /** True while the BLOCK button is held. */
  blocking: boolean;
  /**
   * Frame the BLOCK button was last tapped (justDown), or -1 if no recent tap.
   * Sentinel -1 allows frame 0 taps to be stored unambiguously.
   */
  parryTapFrame: number;
  /** Remaining frames of the post-parry counter window. */
  counterFramesLeft: number;
  /** True for exactly one frame after a successful parry. */
  didParry: boolean;
}

export function createBlockParryState(): BlockParryState {
  return { blocking: false, parryTapFrame: -1, counterFramesLeft: 0, didParry: false };
}

/**
 * Advance the block/parry state one frame.
 * Clears the parry tap if it has expired; decrements the counter window.
 */
export function tickBlockParry(state: BlockParryState, currentFrame: number): void {
  state.didParry = false;
  if (state.counterFramesLeft > 0) state.counterFramesLeft -= 1;
  if (state.parryTapFrame >= 0 && currentFrame - state.parryTapFrame > PARRY_WINDOW) {
    state.parryTapFrame = -1;
  }
}

/** Record a BLOCK tap (justDown). Sets the parry tap frame for window tracking. */
export function notifyBlockTap(state: BlockParryState, frame: number): void {
  state.parryTapFrame = frame;
}

/**
 * Resolve an incoming hit against the current block/parry state.
 *
 * @param currentFrame  The frame the hit lands (from the entity's frame counter).
 * @returns Effective damage/hitstun/knockback after block/parry reduction, plus
 *          a `parried` flag indicating a full parry occurred.
 */
export function resolveBlock(
  state: BlockParryState,
  incomingDamage: number,
  incomingHitstun: number,
  incomingKnockback: number,
  currentFrame: number,
): { damage: number; hitstun: number; knockback: number; parried: boolean } {
  // Parry: tap within PARRY_WINDOW frames before the hit
  if (state.parryTapFrame >= 0 && currentFrame - state.parryTapFrame <= PARRY_WINDOW) {
    state.counterFramesLeft = PARRY_COUNTER_WINDOW;
    state.didParry = true;
    state.parryTapFrame = -1;
    return { damage: 0, hitstun: 0, knockback: 0, parried: true };
  }
  // Block (held): chip damage + reduced stun/knockback
  if (state.blocking) {
    return {
      damage: Math.max(1, Math.ceil(incomingDamage * BLOCK_CHIP_RATIO)),
      hitstun: Math.ceil(incomingHitstun * 0.3),
      knockback: Math.ceil(incomingKnockback * 0.5),
      parried: false,
    };
  }
  // No defense — full hit
  return {
    damage: incomingDamage,
    hitstun: incomingHitstun,
    knockback: incomingKnockback,
    parried: false,
  };
}

/** True while the post-parry counter window is open (free heavy + bonus damage). */
export function isInCounterWindow(state: BlockParryState): boolean {
  return state.counterFramesLeft > 0;
}

/** Apply the post-parry damage bonus multiplier to a damage value. */
export function applyCounterBonus(damage: number): number {
  return Math.round(damage * PARRY_BONUS_MULT);
}
